import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { usePointTo } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { ChapterView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, Card, EmptyState, Loading, Pill, StateBadge, TextField, displayState } from "../../../ui";
import { chapterHref, chapterKeyFromTitle, sectionCounts, sectionWords, sortChapters } from "../model/chapters";

function ChapterCard({ chapter }: { chapter: ChapterView }) {
  const counts = sectionCounts(chapter);
  const state = chapter.element ? displayState(chapter.element.state, { origin: chapter.element.origin }) : null;
  return (
    <li className="nv-chapter">
      <Card aria-labelledby={`chapter-${chapter.chapter_key}`}>
        <h2 id={`chapter-${chapter.chapter_key}`} className="nv-chapter__title">
          <Link to={chapterHref(chapter.chapter_key)}>{chapter.title || chapter.chapter_key}</Link>
        </h2>
        <p className="nv-chapter__meta row">
          <code className="muted">{chapter.chapter_key}</code>
          {state ? <StateBadge state={state} /> : <Pill>{chapter.element ? "In the files" : "Not sent to the SME yet"}</Pill>}
          <span className="muted">{sectionWords(counts)}</span>
        </p>
      </Card>
    </li>
  );
}

/** Starting a chapter is writing its first section, so the form only chooses the key and opens the editor with it. */
function StartChapter({ existing }: { existing: readonly ChapterView[] }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const key = chapterKeyFromTitle(title);
  const taken = existing.find((chapter) => chapter.chapter_key === key);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (key) void navigate(chapterHref(key));
  };

  return (
    <Card aria-labelledby="start-chapter-title">
      <h2 id="start-chapter-title">Start a new chapter</h2>
      <form className="stack" onSubmit={submit}>
        <TextField
          label="Title of the chapter"
          hint="Its key in the address is made from the title. The first section comes next."
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        {key && (
          <p className="muted" role="status">
            {taken ? `A chapter with the key ${key} exists already. This opens it.` : `The key will be ${key}.`}
          </p>
        )}
        <div className="row">
          <Button type="submit" variant="primary" disabled={!key}>
            {taken ? "Open the chapter" : "Start the chapter"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** How long the list stays marked after the agent pointed at it. */
const POINT_MARK_MS = 10_000;

/**
 * The chapters index (CP-17): every specification chapter in the discipline with where it stands, and a way to start a new
 * one. The editor of one chapter is at `/chapters/<key>`. An engineer can start a chapter; an SME can read them.
 */
export function ChaptersPage() {
  const me = useCurrentMe();
  const editable = me.active?.roles.includes("engineer") ?? false;
  const list = useApiQuery("list_chapters", {}, { staleTime: 15_000 });
  const [pointed, setPointed] = useState(false);

  // The agent pointed at the chapters: the list is marked for a moment (a pointer with an element opens that element's page instead).
  usePointTo(() => setPointed(true), { screens: ["chapters"] });
  useEffect(() => {
    if (!pointed) return;
    const timer = setTimeout(() => setPointed(false), POINT_MARK_MS);
    return () => clearTimeout(timer);
  }, [pointed]);

  return (
    <div className="stack" data-pointed={pointed || undefined}>
      <header className="nv-page__head">
        <h1>Chapters</h1>
      </header>
      <p className="muted">
        A chapter is a piece of the specification that people write, in sections. A section stays a draft in the app until it is settled. A chapter that is complete goes to the SME with the rest of the work.
      </p>

      {list.isPending && <Loading>Reading the chapters</Loading>}
      {list.isError && (
        <Card>
          <EmptyState title="The chapters could not be read" action={<Button onClick={() => void list.refetch()}>Try again</Button>}>
            {refusalText(list.error)}
          </EmptyState>
        </Card>
      )}
      {list.data &&
        (list.data.length === 0 ? (
          <Card>
            <EmptyState title="No chapter yet">{editable ? "Start the first one below." : "An engineer starts a chapter when the specification needs one."}</EmptyState>
          </Card>
        ) : (
          <ul className="nv-chapters" aria-label="Chapters">
            {sortChapters(list.data).map((chapter) => (
              <ChapterCard key={chapter.chapter_key} chapter={chapter} />
            ))}
          </ul>
        ))}

      {editable && list.data && <StartChapter existing={list.data} />}
    </div>
  );
}
